import React, { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import type { RetouchingRootState } from '../../../selectors/retouchingState';
import { CheckIcon, PlusIcon } from '@heroicons/react/16/solid';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import formatUSD from '../../../utils/formatUSD';
import { addPhotoToRetouchingLevel } from '../../../actions/v3Order';
import {
  RetouchingChipLevel,
  selectRetouchingChipForPose,
} from '../../../selectors';

// Both chips share one blue, a deeper shade of the retouching screen's
// #3085d6: white or blue text at 12px needs the extra depth for 4.5:1.
const ACCENT = '#1f6fbf';
const ACCENT_DARK = '#195a9c';
const ACCENT_EDGE = '#3085d6';
const ACCENT_TINT = '#eef5fc';

// The menu is positioned over the cart, so opening it never moves the line.
// The chip sits under a thumbnail column (120px at most) and must stay one
// line within it.
const ChipWrapper = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin-top: 6px;
  vertical-align: top;
`;

// One box for both states, so the column does not jump when a photo flips
// between them.
const chipBox = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  border: 1px solid;
  border-radius: 13px;
  text-decoration: none;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
`;

const ChipIcon = styled.span`
  display: inline-flex;
  flex: none;
  width: 12px;
  height: 12px;
`;

const ChipLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The call to action: a solid fill, the one filled thing in the row.
const ChipButton = styled.button`
  ${chipBox}
  color: #fff;
  background: ${ACCENT};
  border-color: ${ACCENT};
  cursor: pointer;
  &:hover,
  &:focus-visible,
  &[aria-expanded='true'] {
    background: ${ACCENT_DARK};
    border-color: ${ACCENT_DARK};
  }
`;

// A status, not a button: a tinted pill that still opens the edit screen.
const ChipLink = styled(Link)`
  ${chipBox}
  color: ${ACCENT};
  background: ${ACCENT_TINT};
  border-color: ${ACCENT_EDGE};
  &:hover,
  &:focus,
  &:focus-visible {
    color: ${ACCENT_DARK};
    background: #e1eefb;
    text-decoration: none;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  min-width: 200px;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  color: #4a4a4a;
  background: none;
  border: none;
  cursor: pointer;
  &:hover,
  &:focus {
    background: #f2f8fd;
  }
`;

interface RetouchingPhotoChipProps {
  orderKey: string | null;
  pose: { id?: number; imageRecordType?: string };
}

// "Premium Retouching" reads as "Premium" under a photo the chip already
// marks as retouched; a product named just "Retouching" keeps its name.
export const shortLevelName = (name: string): string =>
  name.replace(/\s+retouching\s*$/i, '').trim() || name;

const editPath = (orderKey: string) =>
  `/v3/retouching?edit=1&orderKey=${encodeURIComponent(orderKey)}`;

// A cart photo's retouching state: the level it is retouched at (linking to
// that order's retouching screen), or an "Add retouching" chip whose menu adds
// it to a level in place.
const RetouchingPhotoChip: FC<RetouchingPhotoChipProps> = ({
  orderKey,
  pose,
}) => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const history = useHistory();
  const isGroup = pose.imageRecordType === 'group';
  const chip = useSelector((state: RetouchingRootState) =>
    isGroup ? null : selectRetouchingChipForPose(state, orderKey, pose.id),
  );
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const linkRef = useRef<HTMLAnchorElement>(null);
  // Set when a level is chosen in place: the menu item that had focus is gone,
  // so focus moves to whatever the chip became.
  const refocusAfterChoice = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    itemRefs.current[0]?.focus();
    const closeOnOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [open]);

  const chipKind = chip?.kind;
  useEffect(() => {
    if (!refocusAfterChoice.current) return;
    refocusAfterChoice.current = false;
    if (chipKind === 'retouched') linkRef.current?.focus();
    else chipRef.current?.focus();
  });

  if (!chip || !orderKey || pose.id == null) return null;
  const poseId = pose.id;

  if (chip.kind === 'retouched') {
    return (
      <ChipWrapper>
        <ChipLink
          ref={linkRef}
          to={editPath(orderKey)}
          title={chip.levelName}
          aria-label={`${chip.levelName} added. Edit retouching`}
        >
          <ChipIcon aria-hidden="true">
            <CheckIcon />
          </ChipIcon>
          <ChipLabel>{shortLevelName(chip.levelName)}</ChipLabel>
        </ChipLink>
      </ChipWrapper>
    );
  }

  const close = () => {
    setOpen(false);
    chipRef.current?.focus();
  };

  const choose = (level: RetouchingChipLevel) => {
    setOpen(false);
    if (level.opensPicker) {
      history.push(
        `${editPath(orderKey)}&productId=${level.productId}&poseId=${poseId}`,
      );
      return;
    }
    refocusAfterChoice.current = true;
    dispatch(
      addPhotoToRetouchingLevel({
        orderKey,
        poseId,
        productId: level.productId,
        source: 'cart_chip',
      }),
    );
  };

  const onChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = itemRefs.current.filter((item): item is HTMLButtonElement =>
      Boolean(item),
    );
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(current + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <ChipWrapper ref={wrapperRef}>
      <ChipButton
        ref={chipRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={onChipKeyDown}
        // The column has room for one short verb; the name says it in full.
        aria-label="Add retouching"
      >
        <ChipIcon aria-hidden="true">
          <PlusIcon />
        </ChipIcon>
        <ChipLabel>Retouch</ChipLabel>
      </ChipButton>
      {open && (
        <Menu
          role="menu"
          aria-label="Retouching levels"
          onKeyDown={onMenuKeyDown}
        >
          {chip.levels.map((level, index) => (
            <MenuItem
              key={level.productId}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => choose(level)}
            >
              {`${level.name} · ${formatUSD(level.priceCents)}`}
            </MenuItem>
          ))}
        </Menu>
      )}
    </ChipWrapper>
  );
};

export default RetouchingPhotoChip;
